import { useEffect, useRef, useState } from "react";
import { Loader2, PenLine, CornerUpLeft, Send } from "lucide-react";
import ParagraphMap from "@/components/ParagraphMap";
import { otNoPaste } from "@/lib/otPasteGuard";
import { autoGrow, COMPOSE_MIN_PX } from "@/lib/autoGrow";

// the same bold convention the workspace dialogue uses (**one glanceable question**)
function twsText(text) {
  return String(text || "").split(/(\*\*[\s\S]+?\*\*)/g).map((p, i) =>
    (p.startsWith("**") && p.endsWith("**") && p.length > 4)
      ? <strong key={i} className="font-semibold text-stone-900">{p.slice(2, -2).replace(/\*/g, "")}</strong>
      : <span key={i}>{p.replace(/\*(\S[^*\n]*?\S|\S)\*/g, "$1")}</span>);
}

/*
  UDWW Pass 3B — COMPOSE LOCALLY, IN THE STRUCTURE. The writing state of ONE unit / branch, rendered in place of the
  unit's card body inside the map (its position in Introduction / Body / Conclusion is unchanged; its sub-ideas keep
  rendering beneath it as the conceptual structure the writing is developed from). The textarea IS the unit's text
  (authoritative on save); the coaching shown is the unit's own local dialogue; the Paragraph Function Map is a VIEW
  of the unit's own Composition state. Nothing here is a second copy of the student's writing.
*/
export function ComposeUnit({ unit, view, busy, onTurn, onReturn }) {
  const [draft, setDraft] = useState(unit.text || "");
  const [touched, setTouched] = useState(false);
  const [reply, setReply] = useState("");
  const taRef = useRef(null);
  useEffect(() => { if (!touched) setDraft(unit.text || ""); }, [unit.text, touched]);
  // EXPANSIVE WRITING STATE: the page for this part — full width, a useful starting height, grows with the prose
  useEffect(() => { autoGrow(taRef.current, COMPOSE_MIN_PX); }, [draft]);

  const pending = !!(view && view.pending) || busy;
  const target = (view && view.target) || {};
  const region = target.communicative_region || "";
  const wholeText = target.nearest_whole_text || "";
  const coach = ((view && view.dialogue) || []).filter((d) => d.role === "tws" && d.text && !d.pending);
  const last = coach.length ? coach[coach.length - 1] : null;
  const vm = view?.episode?.round1?.visual_map || null;
  const kids = (view && view.children) || [];
  const changed = draft.trim() !== (unit.text || "").trim();

  const submit = () => {
    if (!draft.trim() || pending) return;
    onTurn(draft.trim(), changed ? "revise" : "writing", "");
    setTouched(false);
  };
  const sendReply = () => {
    if (!reply.trim() || pending) return;
    onTurn(null, "answer", reply.trim());
    setReply("");
  };

  return (
    <div data-testid={`compose-unit-${unit.id}`} onClick={(e) => e.stopPropagation()}
      className="mt-1.5 w-full min-w-0 rounded-lg border border-emerald-300/70 bg-emerald-50/40 p-2 sm:p-3 space-y-2.5 cursor-default">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.14em] text-emerald-800">
          <PenLine className="h-2.5 w-2.5" /> refining this paragraph
        </span>
        <span data-testid={`compose-location-${unit.id}`} className="text-[10.5px] text-stone-500 truncate max-w-full">
          {region ? `${region}` : ""}{region && wholeText ? " · " : ""}{wholeText ? `part of: ${wholeText}` : ""}
        </span>
        <button data-testid={`compose-return-${unit.id}`} onClick={onReturn}
          className="ml-auto inline-flex items-center gap-1 text-[10.5px] text-stone-500 hover:text-[#8C3A2A] transition-colors">
          <CornerUpLeft className="h-3 w-3" /> Back to developing
        </button>
      </div>
      <textarea ref={taRef} data-testid={`compose-text-${unit.id}`} {...otNoPaste} value={draft} rows={8}
        onChange={(e) => { setDraft(e.target.value); setTouched(true); autoGrow(e.target, COMPOSE_MIN_PX); }}
        onInput={(e) => autoGrow(e.target, COMPOSE_MIN_PX)}
        placeholder="Shape this paragraph for your reader…"
        className="block w-full min-w-0 resize-none min-h-[220px] max-h-[60vh] rounded-md border border-stone-300 bg-white px-3.5 py-3 text-[14px] leading-[1.7] text-stone-900 outline-none focus:border-emerald-500 transition-colors" />
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid={`compose-submit-${unit.id}`} onClick={submit} disabled={pending || !draft.trim()}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#8C3A2A] text-white text-[11.5px] font-medium px-3 py-1 hover:bg-[#6B2C20] disabled:opacity-40 transition-colors">
          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          {pending ? "TWS is reading this part…" : (changed ? "Save & read with TWS" : "Read with TWS")}
        </button>
        {kids.length ? (
          <span data-testid={`compose-children-note-${unit.id}`} className="text-[10.5px] text-stone-500">
            Your {kids.length} idea{kids.length > 1 ? "s" : ""} under this part stay below — write from them.
          </span>
        ) : null}
      </div>
      {last && (
        <div data-testid={`compose-coach-${unit.id}`} className="rounded-md border border-stone-200 bg-white px-2.5 py-2">
          <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-[#8C3A2A] mb-0.5">The Writing Studio</p>
          <p className="text-[12.5px] leading-relaxed text-stone-800 whitespace-pre-wrap">{twsText(last.text)}</p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <input data-testid={`compose-reply-${unit.id}`} {...otNoPaste} value={reply} onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendReply(); }} placeholder="Reply about this part…"
              className="flex-1 min-w-0 rounded border border-stone-300 px-2 py-1 text-[12px] outline-none focus:border-[#8C3A2A]" />
            <button data-testid={`compose-reply-send-${unit.id}`} onClick={sendReply} disabled={pending || !reply.trim()}
              className="text-[11px] font-semibold text-[#8C3A2A] disabled:opacity-40">Send</button>
          </div>
        </div>
      )}
      {vm ? <div data-testid={`compose-map-${unit.id}`}><ParagraphMap map={vm} isMulti /></div> : null}
    </div>
  );
}
